import extractFiles from "extract-files/extractFiles.mjs";
import isExtractableFile from "extract-files/isExtractableFile.mjs";
import { ClientError, GraphQLClient, RequestDocument, RequestOptions, Variables } from "graphql-request";

type ExtractableFile = File | Blob;

// `extractFiles` deep-clones the whole variables tree to produce its `clone` result. Almost
// every request in the app has no file variables — running a full walker + clone per request
// is wasted work. This cheap recursive check stops at the first match and allocates nothing.
const containsExtractableFile = (value: unknown): boolean => {
  if (value instanceof File || value instanceof Blob) return true;
  if (Array.isArray(value)) return value.some(containsExtractableFile);
  if (value && typeof value === "object") return Object.values(value).some(containsExtractableFile);
  return false;
};

// graphql-request v6+ removed multipart-spec file uploads. This subclass restores them:
// when any variable contains a `File`/`Blob`, it bypasses graphql-request's JSON body
// and sends a request per the GraphQL multipart request spec (operations + map + file parts).
// Otherwise it delegates to the upstream JSON path unchanged.
// Spec: https://github.com/jaydenseric/graphql-multipart-request-spec
export class MultipartGraphQLClient extends GraphQLClient {
  constructor(private readonly endpoint: string) {
    super(endpoint);
  }

  // Match graphql-request's own `T = any` defaults — anything stricter forces callers to
  // pass an explicit type argument at every call site, which breaks the codegen'd hooks.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  request<T = any, V extends Variables = Variables>(options: RequestOptions<V, T>): Promise<T>;
  request<T = any, V extends Variables = Variables>(
    document: RequestDocument,
    variables?: V,
    requestHeaders?: HeadersInit,
  ): Promise<T>;
  async request<T = any>(arg1: RequestDocument | RequestOptions, arg2?: Variables, arg3?: HeadersInit): Promise<T> {
    const isOptions = typeof arg1 === "object" && arg1 !== null && "document" in arg1 && !("kind" in arg1);
    const opts = isOptions
      ? (arg1 as RequestOptions)
      : { document: arg1 as RequestDocument, variables: arg2, requestHeaders: arg3 };

    if (!containsExtractableFile(opts.variables)) {
      return super.request<T>(opts);
    }

    const { clone, files } = extractFiles<ExtractableFile>(
      { query: opts.document, variables: opts.variables ?? {} },
      isExtractableFile,
    );

    const form = new FormData();
    form.append("operations", JSON.stringify(clone));

    const fileEntries = Array.from(files.entries());
    const map = Object.fromEntries(fileEntries.map(([, paths], i) => [String(i), paths]));
    form.append("map", JSON.stringify(map));
    fileEntries.forEach(([file], i) => form.append(String(i), file as File));

    // Do not set Content-Type — the browser appends the multipart boundary.
    const response = await fetch(this.endpoint, { method: "POST", headers: opts.requestHeaders, body: form });
    const responseText = await response.text();
    const parsed = (responseText ? JSON.parse(responseText) : {}) as { data?: T; errors?: unknown[] };

    if (!response.ok || (parsed.errors && parsed.errors.length > 0)) {
      throw new ClientError(
        {
          data: parsed.data,
          errors: (parsed.errors ?? []) as never,
          status: response.status,
          headers: response.headers,
          body: responseText,
        },
        { query: typeof opts.document === "string" ? opts.document : "" },
      );
    }
    return parsed.data as T;
  }
}
